import React, { useContext, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  TextInput,
  Grid,
  Column,
  Accordion,
  AccordionItem,
  DefinitionTooltip,
  Tooltip,
  Layer,
  Tile,
} from '@carbon/react';
import { Information } from '@carbon/react/icons';
import { AxiosError } from 'axios';

import images from '../../../images/images';
import {
  DeploymentEnvironment,
  Gateway,
  NetworkSegment,
  Partition,
  Service,
} from '../../../models/master';
import { VisibilityFlags } from '../../../lib/enums';

import { useApplications } from '../../../hooks/useApplications';
import { useGateways } from '../../../hooks/useGateways';
import {
  useClustersNamespaces,
  useDeploymentEnvsData,
} from '../../../hooks/useDeploymentEnvs';
import { updateNetworkSegment } from '../../../controllers/networksegmentsApi';

import { NotificationContext } from '../../../components/Notifications/Context/NotificationProvider';
import WideTearsheet from '../../../components/WideTearsheet/WideTearsheet';
import { CompactSuggestedTable } from '../../../components/SuggestedTable/SuggestedTable';

import './OffboardNetworkSegment.scss';
import { useNavigate } from 'react-router-dom';

const OffboardNetworkSegment = (props: {
  networkSegment: NetworkSegment | null;
  open: boolean;
  closeTearsheet: () => void;
}) => {
  const { t } = useTranslation('networkSegmentDetails');
  const notification = useContext(NotificationContext);
  const navigate = useNavigate();

  const [inputValue, setInputValue] = useState<string>('');

  const { data: applications } = useApplications(false, true);

  const { data: gateways } = useGateways(VisibilityFlags.MANAGED);

  const { data: environments } = useDeploymentEnvsData(VisibilityFlags.MANAGED);

  const { data: namespaces } = useClustersNamespaces(
    environments?.filter(
      (deplenv: DeploymentEnvironment) => deplenv?.type === 'cluster'
    )
  );

  const getClusterAndNamespace = (envId: string, partitionId: string) => {
    if (envId !== '' && partitionId !== '') {
      const matchedEnv = environments?.find(
        (env: DeploymentEnvironment) => env.resource_id === envId
      );
      const matchedPartition = namespaces?.find(
        (ns: Partition) =>
          ns.resource_id === partitionId && ns.cluster_id === envId
      );

      return `${matchedEnv?.name}/${matchedPartition?.name}`;
    }
    return '—';
  };

  const getFormattedServices = (services: Service[]) => {
    const str = services.slice(0, 3).join(', ');
    const overflow = services.slice(3);

    return (
      <div className='formatted-services'>
        {str}
        {services.length > 3 ? ', ' : ''}
        {services.length > 3 && (
          <DefinitionTooltip
            align='bottom'
            definition={overflow.join('\n')}
            openOnHover
          >
            {`+${overflow.length}`}
          </DefinitionTooltip>
        )}
      </div>
    );
  };

  const formatApplications = () => {
    let formattedApps: any = [];
    if (applications) {
      applications.forEach((app: any) => {
        if (app.network_segment_id === props.networkSegment?.resource_id)
          formattedApps.push({
            id: app.resource_id,
            name: {
              text: app.name,
              link: `/applicationDetails?appId=${app.resource_id}`,
            },
            services: getFormattedServices(
              app.services.map((svc: Service) => svc.name)
            ),
          });
      });
    }
    return formattedApps;
  };

  const formatGateways = () => {
    let gatewayRows: any[] = [];
    gateways?.forEach((gateway: Gateway) => {
      if (gateway.network_segment_id === props.networkSegment?.resource_id) {
        gatewayRows.push({
          id: gateway.resource_id,
          name: {
            text: gateway.name,
            link: `/gatewayDetails?gatewayId=${gateway.resource_id}`,
          },
          environment: getClusterAndNamespace(
            gateway?.deployed_in_depl_env_id ?? '',
            gateway?.deployed_in_partition_id ?? ''
          ),
        });
      }
    });

    return gatewayRows;
  };

  const formattedApplications = useMemo(
    () => formatApplications(),
    [applications]
  );

  const formattedGateways = useMemo(
    () => formatGateways(),
    [gateways, environments, namespaces]
  );

  const handleSubmit = async () => {
    try {
      const payload = {
        unmanaged: true,
        procedure: 'offboard',
        procedural_status: 'in-progress',
      };

      if (props.networkSegment?.resource_id) {
        await updateNetworkSegment(props.networkSegment?.resource_id, payload);

        notification.onTrigger('TOAST', {
          title: t('offboard.successNotification.title'),
          subtitle: t('offboard.successNotification.subtitle', {
            name: props.networkSegment?.name,
          }),
        });

        props.closeTearsheet();
        navigate('/networkSegments', {
          state: {
            reload: true,
          },
        });
      }
    } catch (error) {
      const err = error as AxiosError;
      console.log('There was an error offboarding the network segment', err);
    }
  };

  return (
    <WideTearsheet
      title={t('offboard.title', {
        name: props.networkSegment?.name,
      })}
      open={props.open}
      actions={[
        {
          kind: 'danger',
          label: t('offboard.offboard'),
          onClick: () => handleSubmit(),
          disabled: inputValue !== props.networkSegment?.name,
        },
        {
          kind: 'secondary',
          label: t('offboard.cancel'),
          onClick: () => props.closeTearsheet(),
        },
      ]}
    >
      <Grid className='offboard-network-segment'>
        <Column lg={12}>
          <Tile className='section-tile'>
            <div className='title'>{t('offboard.formTitle')}</div>
            <div>{t('offboard.listTitle')}</div>
            <ul className='list bold'>
              <li className='list-item'>
                <div className='content'>
                  <Trans i18nKey={t('offboard.list.unmanage')} />
                  <Tooltip
                    label={t('offboard.list.unmanageTooltip')}
                    className='info-tooltip'
                  >
                    <Information size={16} />
                  </Tooltip>
                </div>
              </li>
              <li className='list-item'>
                <div className='content'>
                  <Trans i18nKey={t('offboard.list.deleteApps')} />

                  <Tooltip
                    label={t('offboard.list.deleteAppsTooltip')}
                    className='info-tooltip'
                  >
                    <Information size={16} />
                  </Tooltip>
                </div>
              </li>
              <li className='list-item'>
                <div className='content'>
                  <Trans i18nKey={t('offboard.list.deletePolicies')} />

                  <Tooltip
                    label={t('offboard.list.deletePoliciesTooltip')}
                    className='info-tooltip'
                  >
                    <Information size={16} />
                  </Tooltip>
                </div>
              </li>
            </ul>

            <Grid>
              <Column lg={6}>
                <Layer>
                  <TextInput
                    size='md'
                    labelText={
                      <div className='network-segment-name-input'>
                        {t('offboard.networkSegmentInput.label0')}
                        <strong>{props.networkSegment?.name}</strong>
                        {t('offboard.networkSegmentInput.label1')}
                      </div>
                    }
                    placeholder={props.networkSegment?.name}
                    helperText={t('offboard.networkSegmentInput.helperText')}
                    value={inputValue}
                    onChange={(e: any) => setInputValue(e?.target?.value)}
                  />
                </Layer>
              </Column>
            </Grid>
          </Tile>
          <Tile className='section-tile'>
            <div className='header'>{t('offboard.deleteResourcesHeader')}</div>
            <ul className='list'>
              <li>{t('offboard.policies')}</li>
              <li>{t('offboard.remoteConnections')}</li>
            </ul>

            <Accordion className='accordion'>
              <AccordionItem
                title={
                  <div className='accordion-title'>
                    <div className='icon'>{images.applicationIcon()}</div>
                    {t('offboard.applicationsCount', {
                      count: formattedApplications?.length,
                    })}
                  </div>
                }
              >
                <CompactSuggestedTable
                  headers={[
                    {
                      key: 'name',
                      header: t('offboard.tableHeaders.application'),
                    },
                    {
                      key: 'services',
                      header: t('offboard.tableHeaders.service'),
                    },
                  ]}
                  rows={formattedApplications ?? []}
                  rowCount={formattedApplications?.length}
                />
              </AccordionItem>
            </Accordion>
          </Tile>
          <Tile className='section-tile'>
            <div className='header'>
              {t('offboard.unmanageResourcesHeader')}
            </div>

            <Accordion className='accordion'>
              <AccordionItem
                title={
                  <div className='accordion-title'>
                    <div className='icon'>{images.EdgeGatewayIcon()}</div>
                    {t('offboard.gatewaysCount', {
                      count: formattedGateways?.length,
                    })}
                  </div>
                }
              >
                <CompactSuggestedTable
                  headers={[
                    {
                      key: 'name',
                      header: t('offboard.tableHeaders.gateway'),
                    },
                    {
                      key: 'environment',
                      header: t('offboard.tableHeaders.environment'),
                    },
                  ]}
                  rows={formattedGateways ?? []}
                  rowCount={formattedGateways?.length}
                />
              </AccordionItem>
            </Accordion>
          </Tile>
        </Column>
      </Grid>
    </WideTearsheet>
  );
};

export default OffboardNetworkSegment;
